import { getMonthNames } from '@components'

import { Modal, ModalProps, notify, useModal, withModalConditionalRender } from '@billy/design-system'
import { Button, DatePicker, Select, type SelectOption, TextField, YStack } from '@shinetools/lumen-react'
import { format, type Locale } from 'date-fns'
import { useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import type { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { getAccountLabel } from '@views/suppliers/utils/getAccountLabel'

import { DesignSystem } from '../../../../../../../../../../../../enums/designSystem'
import { NotificationKeys } from '../../../../../../../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../../../../../../../enums/queryKeys'
import { Side } from '../../../../../../../../../../../../enums/side'
import { useForm } from '../../../../../../../../../../../../hooks/useForm'
import { useLocale } from '../../../../../../../../../../../../hooks/useLocale'
import { type APIError, NumberValue } from '../../../../../../../../../../../../utils'
import { getValidationErrorAttributes } from '../../../../../../../../../../../../utils/getValidationErrorAttributes'
import { useCreateContactPayment } from './hooks/useCreateContactPayment'
import type { SupplierPaymentFormValues } from './types/supplierPaymentForm'
import { getSupplierPaymentFormDefaultValues } from './utils/getSupplierPaymentFormDefaultValues'
import { getSupplierPaymentFormSchema } from './utils/getSupplierPaymentFormSchema'

// DatePicker labels are Monday-first; date-fns day indexes start on Sunday.
const MONDAY_FIRST_DAY_INDEXES = [1, 2, 3, 4, 5, 6, 0] as const

const getWeekdayLabels = (locale: Locale) => {
  const labels = MONDAY_FIRST_DAY_INDEXES.map((day) => locale.localize?.day(day, { width: 'short' }) ?? '')

  return [labels[0], labels[1], labels[2], labels[3], labels[4], labels[5], labels[6]] as const
}

interface SupplierPaymentModalProps extends ModalProps {
  contact: Pick<Contact, 'id' | 'organizationId'>
  side: Side
}

// Only used to map a bank payment's API error attributes back to this form's fields - kept next
// to handleCreatePaymentError, its one caller, rather than in a shared constants file.
const SUPPLIER_PAYMENT_ATTRIBUTE_FIELDS: Partial<Record<string, keyof SupplierPaymentFormValues>> = {
  cashAccountId: 'accountId',
  cashAmount: 'amount',
  entryDate: 'entryDate',
}

const SupplierPaymentModalComponent = ({ contact, side, ...modalProps }: SupplierPaymentModalProps) => {
  const { t } = useTranslation()
  const locale = useLocale()
  const formId = useId()
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()
  const { close } = useModal(modalProps.id)
  const { accounts, isIdle, isLoading: isAccountsLoading } = useFetchAccounts()

  // Errors the API reports per attribute go next to their field; the toast covers everything else.
  const handleCreatePaymentError = (error?: APIError) => {
    for (const [attribute, message] of Object.entries(getValidationErrorAttributes(error, 'bankPayment'))) {
      const field = SUPPLIER_PAYMENT_ATTRIBUTE_FIELDS[attribute]

      if (field) {
        setError(field, { message })
      }
    }

    notify({
      id: NotificationKeys.ContactPaymentCreated,
      message: t('suppliers.detail.payment.notification.error'),
      variant: 'error',
    })
  }

  const handleCreatePaymentSuccess = async () => {
    await Promise.all([
      queryClient.invalidateQueries([QueryKeys.Bills]),
      queryClient.invalidateQueries([QueryKeys.BillsSummary]),
      queryClient.invalidateQueries([QueryKeys.Contact, contact.id]),
    ])
    notify({
      id: NotificationKeys.ContactPaymentCreated,
      message: t('suppliers.detail.payment.notification.success'),
      variant: 'success',
    })
    close()
  }

  const { create: createPayment, isProcessing } = useCreateContactPayment({
    onError: handleCreatePaymentError,
    onSuccess: handleCreatePaymentSuccess,
  })

  const paymentAccounts = useMemo(
    () => accounts.filter((account) => account.isPaymentEnabled && !account.isArchived),
    [accounts],
  )
  const accountOptions = useMemo<SelectOption[]>(
    () =>
      paymentAccounts.map((account) => ({
        label: getAccountLabel(account),
        value: account.id,
      })),
    [paymentAccounts],
  )
  const dateOrder = locale.code === 'en-US' ? 'mdy' : 'dmy'
  const monthLabels = useMemo(() => getMonthNames(locale.code), [locale.code])
  const weekdayLabels = useMemo(() => getWeekdayLabels(locale), [locale])

  const { Form, FormItem, setError } = useForm({
    defaultValues: useMemo(() => getSupplierPaymentFormDefaultValues(), []),
    designSystem: DesignSystem.Lumen,
    onSubmit: ({ accountId, amount, entryDate }: SupplierPaymentFormValues) => {
      const cashAccount = paymentAccounts.find((account) => account.id === accountId)

      if (!cashAccount) {
        return
      }

      createPayment({
        cashAccountId: cashAccount.id,
        cashAmount: amount,
        cashSide: side,
        contactId: contact.id,
        entryDate: format(entryDate, 'yyyy-MM-dd'),
        organizationId: contact.organizationId,
        subjectCurrencyId: cashAccount.currencyId ?? organization?.baseCurrencyId ?? '',
      })
    },
    validationSchema: useMemo(() => getSupplierPaymentFormSchema(t), [t]),
  })

  const title = side === Side.Credit ? t('contact.index.payment.outgoing') : t('contact.index.payment.ingoing')

  return (
    <Modal autofocusOnInput size="s" {...modalProps}>
      <Modal.Header title={title} />
      <Modal.Body>
        <Form id={formId}>
          <YStack gap={16}>
            <FormItem
              name="entryDate"
              render={({ error, field }) => (
                <DatePicker
                  dateOrder={dateOrder}
                  error={error}
                  label={t('date')}
                  monthLabels={monthLabels}
                  onChange={field.onChange}
                  required
                  todayLabel={t('today')}
                  value={field.value ?? null}
                  weekdayLabels={weekdayLabels}
                />
              )}
            />
            <FormItem
              name="accountId"
              render={({ error, field }) => (
                <Select
                  disabled={isAccountsLoading || isIdle}
                  error={error}
                  label={t('contact.prepayment.account')}
                  onChange={field.onChange}
                  options={accountOptions}
                  placeholder={t('app.accounts.account_select.select_account')}
                  searchable
                  value={field.value}
                />
              )}
            />
            <FormItem
              name="amount"
              render={({ error, field: { onChange, value, ...field } }) => (
                <TextField
                  {...field}
                  error={error}
                  label={t('amount')}
                  onChange={(event) => onChange(new NumberValue(event.target.value).getValue())}
                  placeholder="0.00"
                  required
                  type="number"
                  value={value || ''}
                />
              )}
            />
          </YStack>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color="primary"
          form={formId}
          label={t('approve')}
          loading={isProcessing}
          type="submit"
          variant="filled"
        />
      </Modal.Footer>
    </Modal>
  )
}

export const SupplierPaymentModal = withModalConditionalRender(SupplierPaymentModalComponent)
